import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Shield, FileCheck, Cookie, Scale } from 'lucide-react';
import { LEGAL_PAGES_CONTENT } from '../data/legalContent';
import { IubendaPolicyLink, ExternalRedirect, IUBENDA_PRIVACY_URL, IUBENDA_COOKIE_URL } from '../components/legal/iubenda';

const NAV_ITEMS = [
  { id: 'condizioni', title: 'Condizioni di Vendita', icon: FileCheck },
  { id: 'note-legali', title: 'Note Legali', icon: Scale },
];

/** Documenti ospitati da iubenda: il loro testo non è nel nostro codice. */
const IUBENDA_ITEMS = [
  { id: 'privacy', title: 'Privacy Policy', icon: Shield, href: IUBENDA_PRIVACY_URL },
  { id: 'cookie', title: 'Cookie Policy', icon: Cookie, href: IUBENDA_COOKIE_URL },
];

export default function LegalPage() {
  const { pageId } = useParams();
  if (!pageId) return <Navigate to="/legale/condizioni" replace />;

  const iubendaItem = IUBENDA_ITEMS.find(n => n.id === pageId);
  if (iubendaItem) return <ExternalRedirect to={iubendaItem.href} />;

  const page = LEGAL_PAGES_CONTENT[pageId];
  const navItem = NAV_ITEMS.find(n => n.id === pageId);
  const Icon = navItem?.icon || Shield;

  if (!page) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 text-center">
        <h1 className="text-2xl font-semibold">Pagina non trovata</h1>
        <Link to="/" className="text-sm text-[hsl(var(--dtk-focus))] mt-4 inline-block hover:underline">Torna alla home</Link>
      </div>
    );
  }

  return (
    <div data-testid="legal-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-56 flex-shrink-0">
            <div className="rounded-xl border bg-white p-4 sticky top-20">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Pagine Legali</h3>
              <div className="space-y-1">
                {NAV_ITEMS.map(p => (
                  <Link key={p.id} to={`/legale/${p.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      p.id === pageId ? 'bg-[hsl(210,40%,93%)] text-[hsl(214,58%,41%)] font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                    }`}
                  >
                    <p.icon className="w-4 h-4 flex-shrink-0" />
                    {p.title}
                  </Link>
                ))}
                {IUBENDA_ITEMS.map(p => (
                  <IubendaPolicyLink key={p.id} href={p.href}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  >
                    <p.icon className="w-4 h-4 flex-shrink-0" />
                    {p.title}
                  </IubendaPolicyLink>
                ))}
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="rounded-xl border bg-white p-6 sm:p-8">
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-1">{page.title}</h1>
              <p className="text-xs text-muted-foreground mb-6">Ultimo aggiornamento: {page.lastUpdate}</p>

              <div className="space-y-6">
                {page.sections.map((s, i) => (
                  <div key={i}>
                    <h2 className="text-base font-semibold mb-2">{s.heading}</h2>
                    <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{s.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
